/**
 * The Assistant (vNext-api `docs/copilot.md`): one shared conversation per
 * case, its turns streamed as server-sent events, and what a reply proposes
 * for a person to apply.
 */

export type CopilotMessageStatus = 'running' | 'done' | 'failed' | 'stopped' | 'interrupted';
export type CopilotProposalStatus = 'proposed' | 'applied' | 'dismissed' | 'stale';
export type CopilotToolState = 'running' | 'ok' | 'refused' | 'error';

/** What a reply can propose for the analyst to record as their own: an edit
 * to a document, an ask's answer, a search they ran, a note, a checklist
 * change — and, at setup, the subjects, the report type and the price. */
export type CopilotProposalKind =
  | 'edit'
  | 'answer'
  | 'search'
  | 'note'
  | 'checklist'
  | 'subjects'
  | 'report_type'
  | 'price';

/** A passage the analyst selected in one of the case's documents. */
export interface CopilotPassage {
  document: 'draft' | 'version';
  /** The Word draft's id, or the content version's. */
  documentId: string;
  paraIds: string[];
  quote: string;
  /** Words to start the message with — a review issue handed over to fix. */
  prompt?: string;
}

export type CopilotFocusType =
  | 'checklist'
  | 'evidence'
  | 'ask'
  | 'task'
  | 'review_issue'
  | 'para'
  | 'file';

/** The item open in the workbench, offered with a message. */
export interface CopilotFocus {
  type: CopilotFocusType;
  id: string;
  label: string | null;
}

export interface CopilotSelection {
  passage: CopilotPassage | null;
  focus: CopilotFocus | null;
}

/** A reply as shown, block by block, in order. */
export type CopilotBlock =
  | { type: 'text'; text: string }
  | {
      type: 'tool';
      callId: string;
      name: string;
      label: string;
      state: CopilotToolState;
      summary: string | null;
    }
  | { type: 'task'; taskId: string; agent: string; label: string; status: string }
  | { type: 'ask'; interventionId: string; kind: string; question: string; label: string }
  | { type: 'proposal'; proposalId: string; kind: string; summary: string };

export interface CopilotMessage {
  id: string;
  threadId: string;
  seq: number;
  role: 'analyst' | 'copilot';
  /** A turn of the conversation, or a run's closing statement recorded in it. */
  kind: 'turn' | 'run';
  replyToId: string | null;
  author: string;
  text: string;
  selection: CopilotSelection | null;
  blocks: CopilotBlock[];
  status: CopilotMessageStatus;
  error: string | null;
  model: string | null;
  modelCalls: number;
  costUsd: string;
  createdAt: string | null;
}

export interface CopilotProposal {
  id: string;
  messageId: string;
  kind: CopilotProposalKind | string;
  summary: string;
  target: Record<string, unknown>;
  payload: Record<string, unknown>;
  status: CopilotProposalStatus;
  decidedBy: string | null;
  decidedAt: string | null;
  result: Record<string, unknown> | null;
  error: string | null;
}

export interface CopilotThread {
  id: string;
  status: 'open' | 'closed';
  startedBy: string;
  createdAt: string | null;
  closedAt: string | null;
}

/** A reply running on the case — perhaps another analyst's. */
export interface CopilotRunning {
  messageId: string;
  threadId: string;
  by: string;
  since: string | null;
}

/** What the case has spent against the threshold it runs under (none set:
 * `cap` null), and which threshold. */
export interface CopilotSpend {
  spent: string;
  cap: string | null;
  scope: 'case' | 'before_start' | string;
}

export interface CopilotConversation {
  thread: CopilotThread | null;
  threads: CopilotThread[];
  messages: CopilotMessage[];
  proposals: CopilotProposal[];
  running: CopilotRunning | null;
  spend: CopilotSpend;
  /** A user id → their name. */
  people: Record<string, string>;
  version: string;
}

export type BlockOf<T extends CopilotBlock['type']> = Extract<CopilotBlock, { type: T }>;

/** A turn's events, as the stream carries them. */
export type CopilotEvent =
  | { seq: number; event: 'turn'; data: { threadId: string; askedId: string; messageId: string } }
  | { seq: number; event: 'status'; data: { state: string } }
  | { seq: number; event: 'text'; data: { delta: string } }
  | { seq: number; event: 'reset'; data: Record<string, never> }
  | { seq: number; event: 'tool'; data: Omit<BlockOf<'tool'>, 'type'> }
  | { seq: number; event: 'task'; data: Omit<BlockOf<'task'>, 'type'> }
  | { seq: number; event: 'ask'; data: Omit<BlockOf<'ask'>, 'type'> }
  | { seq: number; event: 'proposal'; data: { proposal: CopilotProposal } }
  | { seq: number; event: 'done'; data: { message: CopilotMessage | null } }
  | {
      seq: number;
      event: 'error';
      data: { reason: string; message: string | null; reply: CopilotMessage | null };
    };

// ---------------------------------------------------------------------------
// The API's shapes (snake_case) and their transforms.
// ---------------------------------------------------------------------------

type Raw = Record<string, unknown>;

const str = (value: unknown): string => (typeof value === 'string' ? value : String(value ?? ''));
const strOrNull = (value: unknown): string | null =>
  value === null || value === undefined ? null : String(value);

export function toCopilotSelection(raw: unknown): CopilotSelection | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const data = raw as Raw;
  const passage = data.passage as Raw | null | undefined;
  const focus = data.focus as Raw | null | undefined;
  return {
    passage: passage
      ? {
          document: passage.document === 'draft' ? 'draft' : 'version',
          documentId: str(passage.document_id),
          paraIds: Array.isArray(passage.para_ids) ? passage.para_ids.map(str) : [],
          quote: str(passage.quote),
        }
      : null,
    focus: focus
      ? {
          type: str(focus.type) as CopilotFocusType,
          id: str(focus.id),
          label: strOrNull(focus.label),
        }
      : null,
  };
}

/** What a message sends with it, in the API's shape. */
export function fromCopilotSelection(selection: CopilotSelection | null): Raw | null {
  if (!selection || (!selection.passage && !selection.focus)) {
    return null;
  }
  return {
    passage: selection.passage
      ? {
          document: selection.passage.document,
          document_id: selection.passage.documentId,
          para_ids: selection.passage.paraIds,
          quote: selection.passage.quote,
        }
      : null,
    focus: selection.focus
      ? { type: selection.focus.type, id: selection.focus.id, label: selection.focus.label }
      : null,
  };
}

export function toCopilotBlock(raw: Raw): CopilotBlock | null {
  switch (raw.type) {
    case 'text':
      return { type: 'text', text: str(raw.text) };
    case 'tool':
      return {
        type: 'tool',
        callId: str(raw.call_id),
        name: str(raw.name),
        label: str(raw.label),
        state: str(raw.state) as CopilotToolState,
        summary: strOrNull(raw.summary),
      };
    case 'task':
      return {
        type: 'task',
        taskId: str(raw.task_id),
        agent: str(raw.agent),
        label: str(raw.label),
        status: str(raw.status),
      };
    case 'ask':
      return {
        type: 'ask',
        interventionId: str(raw.intervention_id),
        kind: str(raw.kind),
        question: str(raw.question),
        label: str(raw.label),
      };
    case 'proposal':
      return {
        type: 'proposal',
        proposalId: str(raw.proposal_id),
        kind: str(raw.kind),
        summary: str(raw.summary),
      };
    default:
      return null;
  }
}

export function toCopilotMessage(raw: Raw): CopilotMessage {
  return {
    id: str(raw.id),
    threadId: str(raw.thread_id),
    seq: Number(raw.seq ?? 0),
    role: raw.role === 'copilot' ? 'copilot' : 'analyst',
    kind: raw.kind === 'run' ? 'run' : 'turn',
    replyToId: strOrNull(raw.reply_to_id),
    author: str(raw.author),
    text: str(raw.text),
    selection: toCopilotSelection(raw.selection),
    blocks: (Array.isArray(raw.blocks) ? (raw.blocks as Raw[]) : [])
      .map(toCopilotBlock)
      .filter((b): b is CopilotBlock => b !== null),
    status: str(raw.status) as CopilotMessageStatus,
    error: strOrNull(raw.error),
    model: strOrNull(raw.model),
    modelCalls: Number(raw.model_calls ?? 0),
    costUsd: str(raw.cost_usd ?? '0'),
    createdAt: strOrNull(raw.created_at),
  };
}

export function toCopilotProposal(raw: Raw): CopilotProposal {
  return {
    id: str(raw.id),
    messageId: str(raw.message_id),
    kind: str(raw.kind),
    summary: str(raw.summary),
    target: (raw.target as Raw | undefined) ?? {},
    payload: (raw.payload as Raw | undefined) ?? {},
    status: str(raw.status) as CopilotProposalStatus,
    decidedBy: strOrNull(raw.decided_by),
    decidedAt: strOrNull(raw.decided_at),
    result: (raw.result as Raw | null | undefined) ?? null,
    error: strOrNull(raw.error),
  };
}

export function toCopilotThread(raw: Raw): CopilotThread {
  return {
    id: str(raw.id),
    status: raw.status === 'closed' ? 'closed' : 'open',
    startedBy: str(raw.started_by),
    createdAt: strOrNull(raw.created_at),
    closedAt: strOrNull(raw.closed_at),
  };
}

export function toCopilotConversation(raw: unknown): CopilotConversation {
  const data = raw as Raw;
  const running = data.running as Raw | null | undefined;
  const spend = (data.spend as Raw | undefined) ?? {};
  return {
    thread: data.thread ? toCopilotThread(data.thread as Raw) : null,
    threads: ((data.threads as Raw[] | undefined) ?? []).map(toCopilotThread),
    messages: ((data.messages as Raw[] | undefined) ?? []).map(toCopilotMessage),
    proposals: ((data.proposals as Raw[] | undefined) ?? []).map(toCopilotProposal),
    running: running
      ? {
          messageId: str(running.message_id),
          threadId: str(running.thread_id),
          by: str(running.by),
          since: strOrNull(running.since),
        }
      : null,
    spend: {
      spent: str(spend.spent ?? '0'),
      cap: strOrNull(spend.cap),
      scope: str(spend.scope ?? 'case'),
    },
    people: (data.people as Record<string, string> | undefined) ?? {},
    version: str(data.version),
  };
}

/** One stream event's data, from the API's shape. */
export function toCopilotEvent(seq: number, event: string, raw: Raw): CopilotEvent | null {
  switch (event) {
    case 'turn':
      return {
        seq,
        event,
        data: {
          threadId: str(raw.thread_id),
          askedId: str(raw.asked_id),
          messageId: str(raw.message_id),
        },
      };
    case 'status':
      return { seq, event, data: { state: str(raw.state) } };
    case 'text':
      return { seq, event, data: { delta: str(raw.delta) } };
    case 'reset':
      return { seq, event, data: {} };
    case 'tool': {
      const block = toCopilotBlock({ ...raw, type: 'tool' });
      return block?.type === 'tool'
        ? {
            seq,
            event,
            data: {
              callId: block.callId,
              name: block.name,
              label: block.label,
              state: block.state,
              summary: block.summary,
            },
          }
        : null;
    }
    case 'task': {
      const block = toCopilotBlock({ ...raw, type: 'task' });
      return block?.type === 'task'
        ? {
            seq,
            event,
            data: {
              taskId: block.taskId,
              agent: block.agent,
              label: block.label,
              status: block.status,
            },
          }
        : null;
    }
    case 'ask': {
      const block = toCopilotBlock({ ...raw, type: 'ask' });
      return block?.type === 'ask'
        ? {
            seq,
            event,
            data: {
              interventionId: block.interventionId,
              kind: block.kind,
              question: block.question,
              label: block.label,
            },
          }
        : null;
    }
    case 'proposal':
      return { seq, event, data: { proposal: toCopilotProposal(raw.proposal as Raw) } };
    case 'done':
      return {
        seq,
        event,
        data: { message: raw.message ? toCopilotMessage(raw.message as Raw) : null },
      };
    case 'error':
      return {
        seq,
        event,
        data: {
          reason: str(raw.reason),
          message: strOrNull(raw.message),
          reply: raw.reply ? toCopilotMessage(raw.reply as Raw) : null,
        },
      };
    default:
      return null;
  }
}
